import { elizaLogger } from '@elizaos/core';

/**
 * Rate limiting algorithms
 */
export enum RateLimitAlgorithm {
  TOKEN_BUCKET = 'TOKEN_BUCKET',
  SLIDING_WINDOW = 'SLIDING_WINDOW',
  FIXED_WINDOW = 'FIXED_WINDOW',
  LEAKY_BUCKET = 'LEAKY_BUCKET',
}

/**
 * Rate limit configuration
 */
export interface RateLimitConfig {
  algorithm: RateLimitAlgorithm;
  maxRequests: number;
  windowMs: number;
  burstLimit?: number;
  refillRate?: number;
  queueSize?: number;
  enableQueue?: boolean;
  enableMetrics?: boolean;
  keyGenerator?: (context: any) => string;
}

/**
 * Rate limit result
 */
export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number;
  retryAfter?: number;
  queuePosition?: number;
}

/**
 * Rate limit metrics
 */
export interface RateLimitMetrics {
  totalRequests: number;
  allowedRequests: number;
  rejectedRequests: number;
  queuedRequests: number;
  averageWaitTime: number;
  peakUsage: number;
  currentUsage: number;
  lastResetTime: number;
}

/**
 * Token bucket rate limiter
 */
export class TokenBucketRateLimiter {
  private tokens: number;
  private lastRefill: number;
  private readonly config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.config = config;
    this.tokens = config.maxRequests;
    this.lastRefill = Date.now();
  }

  /**
   * Check if request is allowed
   */
  isAllowed(tokensRequested: number = 1): RateLimitResult {
    this.refillTokens();

    const allowed = this.tokens >= tokensRequested;

    if (allowed) {
      this.tokens -= tokensRequested;
    }

    return {
      allowed,
      remaining: Math.max(0, this.tokens),
      resetTime: this.getNextResetTime(),
      retryAfter: allowed ? undefined : this.calculateRetryAfter(),
    };
  }

  /**
   * Refill tokens based on time elapsed
   */
  private refillTokens(): void {
    const now = Date.now();
    const elapsed = now - this.lastRefill;
    const refillRate = this.config.refillRate || this.config.maxRequests;
    const tokensToAdd = Math.floor((elapsed / this.config.windowMs) * refillRate);

    if (tokensToAdd > 0) {
      this.tokens = Math.min(this.config.maxRequests, this.tokens + tokensToAdd);
      this.lastRefill = now;
    }
  }

  /**
   * Get next reset time
   */
  private getNextResetTime(): number {
    const refillRate = this.config.refillRate || this.config.maxRequests;
    const timeToFull =
      ((this.config.maxRequests - this.tokens) / refillRate) * this.config.windowMs;
    return Date.now() + timeToFull;
  }

  /**
   * Calculate retry after time
   */
  private calculateRetryAfter(): number {
    const refillRate = this.config.refillRate || this.config.maxRequests;
    return Math.ceil(this.config.windowMs / refillRate);
  }

  /**
   * Reset the bucket
   */
  reset(): void {
    this.tokens = this.config.maxRequests;
    this.lastRefill = Date.now();
  }
}

/**
 * Sliding window rate limiter
 */
export class SlidingWindowRateLimiter {
  private requests: number[] = [];
  private readonly config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.config = config;
  }

  /**
   * Check if request is allowed
   */
  isAllowed(): RateLimitResult {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Remove old requests
    this.requests = this.requests.filter((timestamp) => timestamp > windowStart);

    const allowed = this.requests.length < this.config.maxRequests;

    if (allowed) {
      this.requests.push(now);
    }

    return {
      allowed,
      remaining: Math.max(0, this.config.maxRequests - this.requests.length),
      resetTime: this.getNextResetTime(),
      retryAfter: allowed ? undefined : this.calculateRetryAfter(),
    };
  }

  /**
   * Get next reset time
   */
  private getNextResetTime(): number {
    if (this.requests.length === 0) {
      return Date.now() + this.config.windowMs;
    }
    return this.requests[0] + this.config.windowMs;
  }

  /**
   * Calculate retry after time
   */
  private calculateRetryAfter(): number {
    if (this.requests.length === 0) {
      return 0;
    }
    const oldestRequest = this.requests[0];
    return Math.max(0, oldestRequest + this.config.windowMs - Date.now());
  }

  /**
   * Reset the window
   */
  reset(): void {
    this.requests = [];
  }
}

/**
 * Fixed window rate limiter
 */
export class FixedWindowRateLimiter {
  private requestCount: number = 0;
  private windowStart: number;
  private readonly config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.config = config;
    this.windowStart = Date.now();
  }

  /**
   * Check if request is allowed
   */
  isAllowed(): RateLimitResult {
    const now = Date.now();

    // Check if we need to reset the window
    if (now - this.windowStart >= this.config.windowMs) {
      this.requestCount = 0;
      this.windowStart = now;
    }

    const allowed = this.requestCount < this.config.maxRequests;

    if (allowed) {
      this.requestCount++;
    }

    return {
      allowed,
      remaining: Math.max(0, this.config.maxRequests - this.requestCount),
      resetTime: this.windowStart + this.config.windowMs,
      retryAfter: allowed ? undefined : this.windowStart + this.config.windowMs - now,
    };
  }

  /**
   * Reset the window
   */
  reset(): void {
    this.requestCount = 0;
    this.windowStart = Date.now();
  }
}

/**
 * Request queue for rate limiting
 */
export class RequestQueue {
  private queue: Array<{
    resolve: (result: RateLimitResult) => void;
    reject: (error: Error) => void;
    timestamp: number;
    id: string;
  }> = [];
  private processing: boolean = false;
  private readonly config: RateLimitConfig;
  private readonly rateLimiter:
    | TokenBucketRateLimiter
    | SlidingWindowRateLimiter
    | FixedWindowRateLimiter;

  constructor(config: RateLimitConfig, rateLimiter: any) {
    this.config = config;
    this.rateLimiter = rateLimiter;
  }

  /**
   * Add request to queue
   */
  async enqueue(): Promise<RateLimitResult> {
    return new Promise((resolve, reject) => {
      const queueSize = this.config.queueSize || 100;

      if (this.queue.length >= queueSize) {
        reject(new Error('Queue is full'));
        return;
      }

      this.queue.push({
        resolve,
        reject,
        timestamp: Date.now(),
        id: Math.random().toString(36).substring(7),
      });

      this.processQueue();
    });
  }

  /**
   * Process queued requests
   */
  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const request = this.queue[0];
      const result = this.rateLimiter.isAllowed();

      if (result.allowed) {
        this.queue.shift();
        request.resolve(result);
      } else {
        // Wait for retry after time
        if (result.retryAfter) {
          await this.delay(result.retryAfter);
        } else {
          await this.delay(1000); // Default wait
        }
      }
    }

    this.processing = false;
  }

  /**
   * Get queue status
   */
  getQueueStatus(): {
    queueLength: number;
    processing: boolean;
    averageWaitTime: number;
  } {
    const now = Date.now();
    const averageWaitTime =
      this.queue.length > 0
        ? this.queue.reduce((sum, req) => sum + (now - req.timestamp), 0) / this.queue.length
        : 0;

    return {
      queueLength: this.queue.length,
      processing: this.processing,
      averageWaitTime,
    };
  }

  /**
   * Clear queue
   */
  clear(): void {
    this.queue.forEach((req) => {
      req.reject(new Error('Queue cleared'));
    });
    this.queue = [];
    this.processing = false;
  }

  /**
   * Delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Advanced rate limiter with multiple algorithms and features
 */
export class ZapperRateLimiter {
  private rateLimiters: Map<
    string,
    TokenBucketRateLimiter | SlidingWindowRateLimiter | FixedWindowRateLimiter
  > = new Map();
  private queues: Map<string, RequestQueue> = new Map();
  private metrics: Map<string, RateLimitMetrics> = new Map();
  private readonly config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.config = {
      enableQueue: false,
      enableMetrics: true,
      keyGenerator: (context: any) => context?.key || 'default',
      ...config,
    };
  }

  /**
   * Check if request is allowed
   */
  async checkLimit(context?: any): Promise<RateLimitResult> {
    const key = this.config.keyGenerator!(context);
    const rateLimiter = this.getRateLimiter(key);

    if (this.config.enableMetrics) {
      this.updateMetrics(key, 'request');
    }

    let result: RateLimitResult;

    // Check if queueing is enabled and if the request would be rate limited
    if (this.config.enableQueue) {
      const preCheck = rateLimiter.isAllowed();
      if (!preCheck.allowed) {
        const queue = this.getQueue(key, rateLimiter);
        result = await queue.enqueue();

        if (this.config.enableMetrics) {
          this.updateMetrics(key, 'queued');
        }
      } else {
        result = preCheck;
      }
    } else {
      result = rateLimiter.isAllowed();
    }

    if (this.config.enableMetrics) {
      this.updateMetrics(key, result.allowed ? 'allowed' : 'rejected');
    }

    return result;
  }

  /**
   * Get rate limiter for key
   */
  private getRateLimiter(
    key: string
  ): TokenBucketRateLimiter | SlidingWindowRateLimiter | FixedWindowRateLimiter {
    if (!this.rateLimiters.has(key)) {
      let rateLimiter: TokenBucketRateLimiter | SlidingWindowRateLimiter | FixedWindowRateLimiter;

      switch (this.config.algorithm) {
        case RateLimitAlgorithm.TOKEN_BUCKET:
          rateLimiter = new TokenBucketRateLimiter(this.config);
          break;
        case RateLimitAlgorithm.SLIDING_WINDOW:
          rateLimiter = new SlidingWindowRateLimiter(this.config);
          break;
        case RateLimitAlgorithm.FIXED_WINDOW:
          rateLimiter = new FixedWindowRateLimiter(this.config);
          break;
        default:
          rateLimiter = new TokenBucketRateLimiter(this.config);
      }

      this.rateLimiters.set(key, rateLimiter);
    }

    return this.rateLimiters.get(key)!;
  }

  /**
   * Get queue for key
   */
  private getQueue(key: string, rateLimiter: any): RequestQueue {
    if (!this.queues.has(key)) {
      this.queues.set(key, new RequestQueue(this.config, rateLimiter));
    }
    return this.queues.get(key)!;
  }

  /**
   * Update metrics
   */
  private updateMetrics(
    key: string,
    eventType: 'request' | 'allowed' | 'rejected' | 'queued'
  ): void {
    if (!this.metrics.has(key)) {
      this.metrics.set(key, {
        totalRequests: 0,
        allowedRequests: 0,
        rejectedRequests: 0,
        queuedRequests: 0,
        averageWaitTime: 0,
        peakUsage: 0,
        currentUsage: 0,
        lastResetTime: Date.now(),
      });
    }

    const metrics = this.metrics.get(key)!;

    switch (eventType) {
      case 'request':
        metrics.totalRequests++;
        break;
      case 'allowed':
        metrics.allowedRequests++;
        break;
      case 'rejected':
        metrics.rejectedRequests++;
        break;
      case 'queued':
        metrics.queuedRequests++;
        break;
    }

    // Update current usage - don't call isAllowed() again as it will consume tokens
    const rateLimiter = this.rateLimiters.get(key);
    if (rateLimiter && eventType === 'allowed') {
      // For token bucket, calculate current usage without consuming tokens
      if (rateLimiter instanceof TokenBucketRateLimiter) {
        metrics.currentUsage = this.config.maxRequests - (rateLimiter as any).tokens;
      } else {
        metrics.currentUsage = Math.min(metrics.currentUsage + 1, this.config.maxRequests);
      }
      metrics.peakUsage = Math.max(metrics.peakUsage, metrics.currentUsage);
    }
  }

  /**
   * Get metrics for key
   */
  getMetrics(key?: string): RateLimitMetrics | Map<string, RateLimitMetrics> {
    if (key) {
      return (
        this.metrics.get(key) || {
          totalRequests: 0,
          allowedRequests: 0,
          rejectedRequests: 0,
          queuedRequests: 0,
          averageWaitTime: 0,
          peakUsage: 0,
          currentUsage: 0,
          lastResetTime: Date.now(),
        }
      );
    }
    return this.metrics;
  }

  /**
   * Reset rate limiter for key
   */
  reset(key?: string): void {
    if (key) {
      const rateLimiter = this.rateLimiters.get(key);
      if (rateLimiter) {
        rateLimiter.reset();
      }

      const queue = this.queues.get(key);
      if (queue) {
        queue.clear();
      }

      this.metrics.delete(key);
    } else {
      this.rateLimiters.forEach((rateLimiter) => rateLimiter.reset());
      this.queues.forEach((queue) => queue.clear());
      this.metrics.clear();
    }
  }

  /**
   * Get queue status for key
   */
  getQueueStatus(key?: string): any {
    if (key) {
      const queue = this.queues.get(key);
      return queue ? queue.getQueueStatus() : null;
    }

    const status: any = {};
    this.queues.forEach((queue, k) => {
      status[k] = queue.getQueueStatus();
    });
    return status;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<RateLimitConfig>): void {
    Object.assign(this.config, config);
  }

  /**
   * Get configuration
   */
  getConfig(): RateLimitConfig {
    return { ...this.config };
  }
}

/**
 * API quota manager
 */
export class APIQuotaManager {
  private quotas: Map<
    string,
    {
      daily: number;
      monthly: number;
      used: { daily: number; monthly: number };
      resetTimes: { daily: number; monthly: number };
    }
  > = new Map();
  private readonly config: {
    dailyQuota: number;
    monthlyQuota: number;
    enableMetrics: boolean;
    keyGenerator: (context: any) => string;
  };

  constructor(config: {
    dailyQuota: number;
    monthlyQuota: number;
    enableMetrics?: boolean;
    keyGenerator?: (context: any) => string;
  }) {
    this.config = {
      enableMetrics: true,
      keyGenerator: (context: any) => context?.key || 'default',
      ...config,
    };
  }

  /**
   * Check quota availability
   */
  checkQuota(context?: any): {
    allowed: boolean;
    remaining: { daily: number; monthly: number };
    resetTimes: { daily: number; monthly: number };
  } {
    const key = this.config.keyGenerator(context);
    const quota = this.getQuota(key);

    this.resetQuotasIfNeeded(quota);

    const dailyAllowed = quota.used.daily < quota.daily;
    const monthlyAllowed = quota.used.monthly < quota.monthly;
    const allowed = dailyAllowed && monthlyAllowed;

    return {
      allowed,
      remaining: {
        daily: Math.max(0, quota.daily - quota.used.daily),
        monthly: Math.max(0, quota.monthly - quota.used.monthly),
      },
      resetTimes: quota.resetTimes,
    };
  }

  /**
   * Consume quota
   */
  consumeQuota(amount: number = 1, context?: any): boolean {
    const key = this.config.keyGenerator(context);
    const quota = this.getQuota(key);

    this.resetQuotasIfNeeded(quota);

    // Check if we have sufficient quota in both daily and monthly limits
    const canConsume =
      quota.used.daily + amount <= quota.daily && quota.used.monthly + amount <= quota.monthly;

    if (canConsume) {
      quota.used.daily += amount;
      quota.used.monthly += amount;
      return true;
    }

    return false;
  }

  /**
   * Get quota for key
   */
  private getQuota(key: string) {
    if (!this.quotas.has(key)) {
      const now = Date.now();
      this.quotas.set(key, {
        daily: this.config.dailyQuota,
        monthly: this.config.monthlyQuota,
        used: { daily: 0, monthly: 0 },
        resetTimes: {
          daily: this.getNextDayReset(),
          monthly: this.getNextMonthReset(),
        },
      });
    }
    return this.quotas.get(key)!;
  }

  /**
   * Reset quotas if needed
   */
  private resetQuotasIfNeeded(quota: any): void {
    const now = Date.now();

    if (now >= quota.resetTimes.daily) {
      quota.used.daily = 0;
      quota.resetTimes.daily = this.getNextDayReset();
    }

    if (now >= quota.resetTimes.monthly) {
      quota.used.monthly = 0;
      quota.resetTimes.monthly = this.getNextMonthReset();
    }
  }

  /**
   * Get next day reset time
   */
  private getNextDayReset(): number {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.getTime();
  }

  /**
   * Get next month reset time
   */
  private getNextMonthReset(): number {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1);
    nextMonth.setHours(0, 0, 0, 0);
    return nextMonth.getTime();
  }

  /**
   * Get quota status
   */
  getQuotaStatus(key?: string): any {
    if (key) {
      const quota = this.quotas.get(key);
      return quota
        ? {
            daily: {
              limit: quota.daily,
              used: quota.used.daily,
              remaining: quota.daily - quota.used.daily,
            },
            monthly: {
              limit: quota.monthly,
              used: quota.used.monthly,
              remaining: quota.monthly - quota.used.monthly,
            },
            resetTimes: quota.resetTimes,
          }
        : null;
    }

    const status: any = {};
    this.quotas.forEach((quota, k) => {
      status[k] = {
        daily: {
          limit: quota.daily,
          used: quota.used.daily,
          remaining: quota.daily - quota.used.daily,
        },
        monthly: {
          limit: quota.monthly,
          used: quota.used.monthly,
          remaining: quota.monthly - quota.used.monthly,
        },
        resetTimes: quota.resetTimes,
      };
    });
    return status;
  }

  /**
   * Reset quotas
   */
  resetQuota(key?: string): void {
    if (key) {
      const quota = this.quotas.get(key);
      if (quota) {
        quota.used.daily = 0;
        quota.used.monthly = 0;
      }
    } else {
      this.quotas.forEach((quota) => {
        quota.used.daily = 0;
        quota.used.monthly = 0;
      });
    }
  }
}

/**
 * Rate limiting middleware for GraphQL client
 */
export class RateLimitMiddleware {
  private rateLimiter: ZapperRateLimiter;
  private quotaManager: APIQuotaManager;

  constructor(
    rateLimitConfig: RateLimitConfig,
    quotaConfig: {
      dailyQuota: number;
      monthlyQuota: number;
      enableMetrics?: boolean;
      keyGenerator?: (context: any) => string;
    }
  ) {
    this.rateLimiter = new ZapperRateLimiter(rateLimitConfig);
    this.quotaManager = new APIQuotaManager(quotaConfig);
  }

  /**
   * Check if request is allowed
   */
  async checkRequest(context?: any): Promise<{
    allowed: boolean;
    rateLimit: RateLimitResult;
    quota: any;
  }> {
    const [rateLimit, quota] = await Promise.all([
      this.rateLimiter.checkLimit(context),
      Promise.resolve(this.quotaManager.checkQuota(context)),
    ]);

    const allowed = rateLimit.allowed && quota.allowed;

    // Only consume quota if the request is allowed by both rate limiter and quota manager
    if (allowed) {
      this.quotaManager.consumeQuota(1, context);
    }

    return {
      allowed,
      rateLimit,
      quota,
    };
  }

  /**
   * Get comprehensive status
   */
  getStatus(): {
    rateLimit: any;
    quota: any;
  } {
    return {
      rateLimit: {
        metrics: this.rateLimiter.getMetrics(),
        queueStatus: this.rateLimiter.getQueueStatus(),
        config: this.rateLimiter.getConfig(),
      },
      quota: this.quotaManager.getQuotaStatus(),
    };
  }

  /**
   * Reset all limits
   */
  reset(): void {
    this.rateLimiter.reset();
    this.quotaManager.resetQuota();
  }
}

/**
 * Default export for convenience
 */
export default {
  ZapperRateLimiter,
  APIQuotaManager,
  RateLimitMiddleware,
  RateLimitAlgorithm,
  TokenBucketRateLimiter,
  SlidingWindowRateLimiter,
  FixedWindowRateLimiter,
};
